import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { $generateRewardsStatementsStore, $profileStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import { Button, Select, Spinner, Tabs } from '@shared/widgets';

const REWARDS_START_YEAR = 2026;
const NOW = dayjs();
const CURRENT_YEAR = NOW.year();
const CURRENT_MONTH = NOW.month() + 1;

const StatementTabs = {
  Rewards: 'Rewards',
} as const;
type StatementTabs = (typeof StatementTabs)[keyof typeof StatementTabs];

interface FormData {
  month: string;
  year: string;
}
const DEFAULT_VALUES: FormData = {
  month: String(CURRENT_MONTH).padStart(2, '0'),
  year: CURRENT_YEAR.toString(),
};

interface RewardsTabProps {
  className?: string;
}

export const RewardsTab = (props: RewardsTabProps) => {
  const profileStore = useUnit($profileStore.store);

  const [tab, setTab] = useState<StatementTabs>(StatementTabs.Rewards);

  const { control, formState, handleSubmit, watch, setValue } = useForm<FormData>({ defaultValues: DEFAULT_VALUES });
  const [month, year] = watch(['month', 'year']);

  const { t } = useTranslation('statements');

  const submitHandler = async (data: FormData) => {
    const fromInclusive = DateUtils.getFirstDateOfYear(data.year, data.month);
    await $generateRewardsStatementsStore.events.generateRewardsStatements({
      fromInclusive,
      toExclusive: dayjs(fromInclusive).add(1, 'month').toISOString(),
    });
  };

  const yearOptions = useMemo(() => {
    const onboarding = dayjs(profileStore.data?.onboardingDate || null);
    const onboardingYear = onboarding.isValid() ? onboarding.year() : REWARDS_START_YEAR;
    const startYear = Math.max(REWARDS_START_YEAR, onboardingYear);
    return DateUtils.getYearsList(startYear).map((year) => ({ label: year.toString(), value: year.toString() }));
  }, [profileStore.data?.onboardingDate]);
  useEffect(() => {
    if (yearOptions.length === 0) return;
    if (!yearOptions.some((option) => option.value === year)) setValue('year', yearOptions[0]!.value);
  }, [yearOptions, year, setValue]);

  const monthOptions = useMemo(() => {
    const onboarding = dayjs(profileStore.data?.onboardingDate || null);
    const onboardingYear = onboarding.isValid() ? onboarding.year() : REWARDS_START_YEAR;
    const startYear = Math.max(REWARDS_START_YEAR, onboardingYear);
    const selectedYear = Number(year);

    return DateUtils.getMonthsSelectList({
      minimalMonth: selectedYear === startYear && onboarding.isValid() && onboardingYear >= REWARDS_START_YEAR ? onboarding.month() + 1 : 1,
      maximalMonth: selectedYear === CURRENT_YEAR ? CURRENT_MONTH : 12,
    }).map((month) => ({ label: month.label, value: month.value }));
  }, [year, profileStore.data?.onboardingDate]);
  useEffect(() => {
    if (monthOptions.length === 0) return;
    if (!monthOptions.some((option) => option.value === month)) setValue('month', monthOptions[0]!.value);
  }, [monthOptions, month, setValue]);

  const tabs = useMemo(() => [{ label: 'Rewards Statement', value: StatementTabs.Rewards }], []);
  return (
    <section className={cn(props.className)}>
      <Spinner show={formState.isSubmitting} position />
      {PaletteUtils.isInsigniaPalette && (
        <Tabs
          className='w-full'
          listClassName='gap-0'
          labelClassName='pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black'
          options={tabs}
          value={tab}
          onChange={setTab}
        />
      )}
      <div
        className={cn(
          'flex flex-col gap-6 bg-white p-6 dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
        )}
      >
        {PaletteUtils.isVcfcPalette && <Tabs listClassName='gap-12' options={tabs} value={tab} onChange={setTab} />}
        <form noValidate onSubmit={handleSubmit(submitHandler)}>
          <div className='flex items-center justify-center gap-2'>
            <Controller
              name='month'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Select className='min-w-[130px]' items={monthOptions} value={value} onChange={onChange} error={error?.message} />
              )}
            />
            <Controller
              name='year'
              control={control}
              rules={{ required: t('errors.form.required', { ns: 'common' }) }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Select items={yearOptions} value={value} onChange={onChange} error={error?.message} />
              )}
            />
          </div>
          <Button className='mx-auto mt-7 max-w-[408px]' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('payment.buttons.generate')}
          </Button>
        </form>
      </div>
    </section>
  );
};
